import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { quantPaperPlaceOrder, getErrorMessage } from '../../lib/api';

/**
 * Dialog per piazzare un ordine paper Alpaca.
 *
 * Props:
 *  - open, onOpenChange
 *  - symbol: es. "SPY"
 *  - strategy: string|null — incluso nel payload al backend
 *  - lastPrice: number — usato per prefill e preview notional
 *  - accountEquity: number — usato per suggerire qty in base a maxPctPerPosition
 *  - maxPctPerPosition: number (es. 0.20 = 20%) — percentuale equity per trade
 *  - signalSnapshot: object|null — snapshot del segnale live, inoltrato al backend
 *  - onPlaced: callback(result) chiamato dopo successo
 */
const PlaceOrderDialog = ({
  open,
  onOpenChange,
  symbol = '',
  strategy = null,
  lastPrice = 0,
  accountEquity = 0,
  maxPctPerPosition = 0.2,
  signalSnapshot = null,
  onPlaced,
}) => {
  const [side, setSide] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [limitPrice, setLimitPrice] = useState('');
  const [qty, setQty] = useState('');
  const [timeInForce, setTimeInForce] = useState('day');
  const [submitting, setSubmitting] = useState(false);

  // Prefill qty e limit price quando si apre il dialog
  useEffect(() => {
    if (!open) return;
    // qty suggerita = floor((equity * pct) / prezzo)
    const suggestedQty =
      lastPrice > 0 && accountEquity > 0
        ? Math.floor((accountEquity * maxPctPerPosition) / lastPrice)
        : 1;
    setQty(String(Math.max(1, suggestedQty)));
    setLimitPrice(lastPrice > 0 ? lastPrice.toFixed(2) : '');
    setSide('buy');
    setOrderType('market');
    setTimeInForce('day');
    setSubmitting(false);
  }, [open, lastPrice, accountEquity, maxPctPerPosition]);

  const parsedQty = parseInt(qty, 10) || 0;
  const parsedLimit = parseFloat(limitPrice) || 0;
  const effectivePrice = orderType === 'limit' && parsedLimit > 0 ? parsedLimit : lastPrice;
  const notional = parsedQty * effectivePrice;
  const pctEquity = accountEquity > 0 ? (notional / accountEquity) * 100 : 0;

  const handleSubmit = async () => {
    if (parsedQty <= 0) {
      toast.error('Quantità non valida');
      return;
    }
    if (orderType === 'limit' && parsedLimit <= 0) {
      toast.error('Inserisci un prezzo limite valido');
      return;
    }

    const payload = {
      symbol,
      qty: parsedQty,
      side,
      type: orderType,
      time_in_force: timeInForce,
      strategy,
      signal_snapshot: signalSnapshot,
    };
    if (orderType === 'limit') {
      payload.limit_price = parsedLimit;
    }

    setSubmitting(true);
    try {
      const result = await quantPaperPlaceOrder(payload);
      toast.success(`Ordine ${side.toUpperCase()} ${parsedQty}x ${symbol} inviato`, {
        description: `ID: ${result.data?.id || '—'} · Tipo: ${orderType}`,
        duration: 6000,
      });
      onPlaced?.(result.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Ordine fallito'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            Piazza ordine · <code className="font-mono">{symbol}</code>
          </DialogTitle>
          <DialogDescription>
            Ordine paper Alpaca.
            {strategy && (
              <> Strategia: <Badge variant="outline" className="text-[10px] ml-1">{strategy}</Badge></>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Side (MVP: solo Buy abilitato) */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Direzione</Label>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => setSide('buy')}
                className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${
                  side === 'buy'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-muted/30 text-foreground border-border hover:bg-muted/60'
                }`}
              >
                Buy
              </button>
              <button
                disabled
                title="Sell non disponibile in MVP"
                className="flex-1 py-2 rounded-md text-sm font-medium border border-border bg-muted/20 text-muted-foreground cursor-not-allowed opacity-50"
              >
                Sell
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Sell disponibile in Sprint 2.</p>
          </div>

          {/* Tipo ordine */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tipo</Label>
            <Select value={orderType} onValueChange={setOrderType}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="market">Market</SelectItem>
                <SelectItem value="limit">Limit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Limit price (solo se type=limit) */}
          {orderType === 'limit' && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Prezzo limite ($)
              </Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                className="mt-1.5 font-mono"
                placeholder="0.00"
              />
            </div>
          )}

          {/* Quantità */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Quantità (azioni)
            </Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="mt-1.5 font-mono"
              placeholder="1"
            />
          </div>

          {/* Time in force */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Time in force
            </Label>
            <Select value={timeInForce} onValueChange={setTimeInForce}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="gtc">GTC (Good Till Cancelled)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preview notional */}
          {parsedQty > 0 && effectivePrice > 0 && (
            <div className="rounded-lg bg-muted/40 border p-3 text-sm space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-2">
                Preview ordine
              </p>
              <div className="flex justify-between tabular-nums">
                <span className="text-muted-foreground">Stima notional:</span>
                <span className="font-semibold">${notional.toFixed(2)}</span>
              </div>
              <div className="flex justify-between tabular-nums">
                <span className="text-muted-foreground">Stima % equity:</span>
                <span className={`font-semibold ${pctEquity > 25 ? 'text-amber-600' : 'text-foreground'}`}>
                  {pctEquity.toFixed(2)}%
                </span>
              </div>
              {lastPrice > 0 && (
                <div className="flex justify-between tabular-nums text-[11px] text-muted-foreground mt-1 pt-1 border-t">
                  <span>Prezzo corrente:</span>
                  <span className="font-mono">${lastPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || parsedQty <= 0}
            className="min-w-[120px]"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Invio…</>
            ) : (
              <><ShoppingCart className="w-4 h-4 mr-1.5" /> Piazza ordine</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlaceOrderDialog;
